import '@todo/core/scroll-restoration';

import { DefaultTheme, Stack, ThemeProvider } from 'expo-router';
import Head from 'expo-router/head';
import * as SplashScreen from 'expo-splash-screen';
import { useEffect } from 'react';
import { I18nextProvider } from 'react-i18next';

import { AnimatedSplashOverlay } from '@/components/animated-icon';
import { rememberAiReferral } from '@todo/core/ai-referral';
import i18n from '@/lib/i18n';
import { SITE_NAME, webSiteJsonLd } from '@todo/core/seo';

SplashScreen.preventAutoHideAsync();

// Base 'en' instance — only reaches the un-prefixed root redirect page and
// +not-found (see src/app/index.tsx). Every real page lives under
// app/[lang]/, whose layout provides the locale-scoped instance that shadows
// this one for its whole subtree.
export default function RootLayout() {
  // Once per page load: note whether the visit came from an AI assistant, for
  // the `via` property of affiliate clicks (web only).
  useEffect(rememberAiReferral, []);

  return (
    <I18nextProvider i18n={i18n}>
      <ThemeProvider value={DefaultTheme}>
      {/* Site-wide SEO defaults (English) — a last-resort fallback only.
          Managed by react-helmet (expo-router/head) so more specific <Head>
          tags in app/[lang]/** override them and no duplicate <title>/<meta>
          leaks through. No-op on native. */}
      <Head>
        <title>{`${SITE_NAME} — Things to do on the French Riviera`}</title>
        <meta
          name="description"
          content="Things to do on the French Riviera: concerts and shows, day trips, wine tastings and boat rentals in Nice, Cannes, Monaco, Antibes and Saint-Tropez."
        />
        <meta property="og:type" content="website" />
        {/* Site-level entity. It belongs on every page (it describes the site,
            not the document) and the root layout wraps every route, so this is
            the one place it's emitted. Organization needs the locale for its
            description, so it lives in app/[lang]/_layout.tsx; per-page Event/
            Product/Article/BreadcrumbList blocks are added by the detail heads. */}
        <script type="application/ld+json">{JSON.stringify(webSiteJsonLd())}</script>
      </Head>
      <AnimatedSplashOverlay />
      {/* Analytics is web-only and cookieless: the Umami tracker is injected
          statically in the document <head> (src/app/+html.tsx) and auto-tracks
          client-side route changes, so no React component or native no-op is
          needed here. */}
      <Stack screenOptions={{ headerShown: false }} />
      </ThemeProvider>
    </I18nextProvider>
  );
}
